import type {
  CaptureFailureCode,
  CaptureUpload,
} from 'ts-components/PiChat/protocol';
import { redactUrl, resolveUploadUrl } from './captureUrl';

const MAX_FAILURE_MESSAGE_CHARS = 300;

export type CaptureOutcome =
  | { ok: true; blob: Blob }
  | { ok: false; code: CaptureFailureCode; message?: string };

export type CaptureUploadStatus =
  | 'png_uploaded'
  | 'failure_uploaded'
  | 'expired'
  | 'failed';

interface ResolvedTarget {
  url: string;
  headers: Record<string, string>;
}

export interface ResolvedCaptureUpload {
  png: ResolvedTarget;
  failure: ResolvedTarget;
  expiresAt: number;
}

export type Warn = (message: string) => void;

const defaultWarn: Warn = (message) => {
  // eslint-disable-next-line no-console -- a refused or failed upload has no other trace on the client
  console.warn(message);
};

export const resolveCaptureUpload = (
  { png, failure, expiresAt }: CaptureUpload,
  wsUrl: string,
  warn: Warn = defaultWarn
): ResolvedCaptureUpload | null => {
  const pngUrl = resolveUploadUrl(png.url, wsUrl);
  const failureUrl = resolveUploadUrl(failure.url, wsUrl);
  if (!pngUrl || !failureUrl) {
    const refused = [png, failure]
      .filter((_, i) => !(i === 0 ? pngUrl : failureUrl))
      .map((target) => redactUrl(target.url))
      .join(', ');
    warn(`block.capture refused: upload URL not allowed (${refused})`);
    return null;
  }
  return {
    png: { url: pngUrl, headers: png.headers },
    failure: { url: failureUrl, headers: failure.headers },
    expiresAt,
  };
};

type PutResult = 'stored' | 'rejected' | 'expired';

export interface SendCaptureOutcomeDeps {
  fetch?: typeof fetch;
  warn?: Warn;
}

export const sendCaptureOutcome = (
  upload: ResolvedCaptureUpload,
  outcome: CaptureOutcome,
  {
    fetch: fetchImpl = (...args) => globalThis.fetch(...args),
    warn = defaultWarn,
  }: SendCaptureOutcomeDeps = {}
): Promise<CaptureUploadStatus> => {
  const remainingMs = upload.expiresAt - Date.now();
  if (remainingMs <= 0) {
    warn('block.capture not answered: its upload URLs have expired');
    return Promise.resolve('expired');
  }

  const expiry = new AbortController();
  const timer = setTimeout(() => expiry.abort(), remainingMs);

  const put = async (
    target: ResolvedTarget,
    body: Blob,
    what: 'PNG' | 'failure'
  ): Promise<PutResult> => {
    try {
      const response = await fetchImpl(target.url, {
        method: 'PUT',
        headers: { ...target.headers },
        body,
        signal: expiry.signal,
        credentials: 'omit',
        redirect: 'error',
      });
      if (response.ok || response.status === 412) return 'stored';
      warn(
        `block.capture ${what} upload to ${redactUrl(target.url)} failed: HTTP ${response.status}`
      );
      return 'rejected';
    } catch (error) {
      if (expiry.signal.aborted) {
        warn(`block.capture ${what} upload expired before it finished`);
        return 'expired';
      }
      const name = error instanceof Error ? error.name : 'Error';
      warn(
        `block.capture ${what} upload to ${redactUrl(target.url)} failed: ${name}`
      );
      return 'rejected';
    }
  };

  const putFailure = async (
    code: CaptureFailureCode,
    message: string | undefined
  ): Promise<CaptureUploadStatus> => {
    const body = JSON.stringify({
      code,
      message: message?.slice(0, MAX_FAILURE_MESSAGE_CHARS),
    });
    const result = await put(upload.failure, new Blob([body]), 'failure');
    if (result === 'stored') return 'failure_uploaded';
    return result === 'expired' ? 'expired' : 'failed';
  };

  const run = async (): Promise<CaptureUploadStatus> => {
    if (!outcome.ok) return putFailure(outcome.code, outcome.message);
    const result = await put(upload.png, new Blob([outcome.blob]), 'PNG');
    if (result === 'stored') return 'png_uploaded';
    if (result === 'expired') return 'expired';
    return putFailure('capture_failed', 'Uploading the PNG failed');
  };

  return run().finally(() => clearTimeout(timer));
};
